import { createContext, useContext, useEffect, useRef, useState } from "react";
import { ActionList, ActionMenu, FormControl, Select, Text, TextInput } from "@primer/react";
import { ChevronRightIcon, NumberIcon, PencilIcon, PlusIcon, TrashIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AllocationMap } from "../components/AllocationMap/AllocationMap.jsx";
import { AssetDecorations } from "../components/AssetDecorations/AssetDecorations.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceImpact } from "../components/ComplianceMetric/ComplianceMetric.jsx";
import { ExpansionSection } from "../components/ExpansionSection/ExpansionSection.jsx";
import { EditablePercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { FormBody, FormContainer, FormFooter } from "../components/FormContainer/FormContainer.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { PortfolioValueDialog } from "../components/PortfolioValue/PortfolioValueDialog.jsx";
import { propertyHref } from "../components/PropertyIdentity/PropertyIdentity.jsx";
import { PropertyValueIdentity, propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { sortProfilesByName } from "../lib/sortProfilesByName.js";
import { assetComplianceImpacts, evaluateCompliance } from "../domain/compliance.js";
import {
  createBrowserView, deleteBrowserView, effectiveValue, updateAllocationSnapshot, updateBrowserView,
  validateBrowserViewDraft
} from "../domain/document.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import { usePresentationState } from "../storage/presentationState.js";
import "./BrowserPage.css";

export function BrowserPage({ profileId: profileIdOverride, repository, viewId }) {
  const application = useApplicationDocument(repository);
  const profileId = application.profile?.id ?? profileIdOverride;

  if (application.error && !application.document) {
    return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  }
  if (!application.document) return <BrowserLoading />;
  if (viewId === "new") return <NewViewPage application={application} />;
  return <ViewPage application={application} profileId={profileId} viewId={viewId} />;
}

const defaultView = { id: null, name: "All assets", groupBy: [], displayProperties: [], sort: "allocation-desc", filter: null };
const defaultViewStorageId = "all";
const AllocationEditingContext = createContext(null);
const AssetImpactContext = createContext(new Map());

function NewViewPage({ application }) {
  const { document } = application;
  const [draft, setDraft] = useState(() => ({ name: "", groupBy: document.properties[0] ? [document.properties[0].id] : [], displayProperties: [], sort: "allocation-desc", filter: null }));
  const [saveError, setSaveError] = useState(null);

  async function saveView() {
    setSaveError(null);
    try {
      const nextDocument = createBrowserView(document, draft);
      if (await application.save(nextDocument)) window.location.hash = `#/browser/${nextDocument.browserViews.at(-1).id}`;
    } catch (error) {
      setSaveError(error.message);
    }
  }

  return (
    <article className="browser-page">
      <PageHeading description="Configure a reusable portfolio view." actions={<Button as="a" href="#/browser" tone="secondary">Back to Browser</Button>}>New view</PageHeading>
      {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
      <AllocationWarning document={document} />
      <ViewEditor document={document} draft={draft} onCancel={() => { window.location.hash = "#/browser"; }} onChange={setDraft} onSave={saveView} saveError={saveError} saving={application.saving} />
      <ViewResults document={document} setup={draft} title="Preview" />
    </article>
  );
}

function ViewPage({ application, profileId, viewId }) {
  const { document } = application;
  const views = document.browserViews ?? [];
  const [rememberedViewId, setRememberedViewId] = usePresentationState({
    defaultValue: window.localStorage.getItem("regulator.browser.lastViewId") ?? defaultViewStorageId, lifetime: "persistent", name: "selected-view", page: "browser", profileId, scope: "profile", validate: stringValue
  });
  let view = defaultView;
  if (viewId && viewId !== defaultViewStorageId) view = views.find((item) => item.id === viewId);
  if (!viewId && rememberedViewId && rememberedViewId !== defaultViewStorageId) {
    view = views.find((item) => item.id === rememberedViewId) ?? defaultView;
  }
  if (!view) return <MissingView />;
  return <ExistingViewPage application={application} key={`${profileId}:${view.id}`} onViewSelected={setRememberedViewId} profileId={profileId} view={view} />;
}

function ExistingViewPage({ application, onViewSelected, profileId, view }) {
  const { document } = application;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [portfolioValueOpen, setPortfolioValueOpen] = useState(false);
  const optionsButton = useRef(null);
  const allocationSaveInProgress = useRef(false);
  const [allocationOrder, setAllocationOrder] = useState(document);
  const [editAllocations, setEditAllocations] = usePresentationState({
    defaultValue: false, lifetime: "session", name: "edit-allocations", page: "browser", profileId, scope: "profile", validate: booleanValue
  });
  const [showUnallocated, setShowUnallocated] = usePresentationState({
    defaultValue: false, lifetime: "persistent", name: "show-unallocated", page: "browser", profileId, scope: "profile", validate: booleanValue
  });
  const [comparisonProfileId, setComparisonProfileId] = usePresentationState({
    defaultValue: null, lifetime: "session", name: "comparison-profile", page: "browser", profileId, scope: "profile", validate: nullableStringValue
  });
  const comparisonProfile = application.profiles?.find((profile) => profile.id === comparisonProfileId) ?? null;

  useEffect(() => {
    onViewSelected(view.id ?? defaultViewStorageId);
  }, [onViewSelected, view.id]);

  function startEditing() {
    setDraft(browserViewDraft(view));
    setSaveError(null);
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setConfirmDelete(false);
  }

  async function saveView() {
    setSaveError(null);
    try {
      if (await application.save(updateBrowserView(document, view.id, draft))) cancelEditing();
    } catch (error) {
      setSaveError(error.message);
    }
  }

  async function removeView() {
    if (await application.save(deleteBrowserView(document, view.id))) {
      onViewSelected(defaultViewStorageId);
      window.location.hash = "#/browser";
    }
  }

  const setup = editing ? draft : view;

  function changeAllocationEditing(enabled) {
    setAllocationOrder(document);
    setEditAllocations(enabled);
  }

  async function saveAllocation(assetId, basisPoints) {
    if (allocationSaveInProgress.current || application.saving) return false;
    allocationSaveInProgress.current = true;
    try {
      const allocations = document.allocations.filter((allocation) => allocation.assetId !== assetId);
      return await application.save(updateAllocationSnapshot(document, [...allocations, { assetId, basisPoints }]));
    } finally {
      allocationSaveInProgress.current = false;
    }
  }

  return (
    <article className="browser-page">
      <PageHeading
        description={view.id ? viewDescription(document, view) : "Portfolio assets sorted by allocation."}
        actions={<ViewActions activeProfileId={profileId} comparisonProfile={comparisonProfile} comparisonProfileId={comparisonProfileId} document={document} editing={editing} editAllocations={editAllocations} onComparisonProfileChange={setComparisonProfileId} onEdit={startEditing} onEditAllocationsChange={changeAllocationEditing} onPortfolioValue={() => setPortfolioValueOpen(true)} onShowUnallocatedChange={setShowUnallocated} optionsButtonRef={optionsButton} profiles={application.profiles ?? []} saving={application.saving} showUnallocated={showUnallocated} view={view} />}
      >Browser</PageHeading>
      {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
      <AllocationWarning document={document} editing={editAllocations && !editing} />
      {editing ? (
        <section className="view-setup">
          <ViewEditor document={document} draft={draft} existingView={view} onCancel={cancelEditing} onChange={setDraft} onDelete={() => setConfirmDelete(true)} onSave={saveView} saveError={saveError} saving={application.saving} />
          {confirmDelete ? <DeleteViewConfirmation onCancel={() => setConfirmDelete(false)} onConfirm={removeView} saving={application.saving} view={view} /> : null}
        </section>
      ) : null}
      <AllocationEditingContext.Provider value={editAllocations && !editing ? { saveAllocation, saving: application.saving, orderDocument: allocationOrder } : null}>
        <RememberedViewResults comparisonProfile={comparisonProfile} document={document} profile={application.profile} profileId={profileId} setup={setup} showUnallocated={showUnallocated} viewId={view.id ?? defaultViewStorageId} />
      </AllocationEditingContext.Provider>
      {portfolioValueOpen ? <PortfolioValueDialog onClose={() => setPortfolioValueOpen(false)} returnFocusRef={optionsButton} /> : null}
    </article>
  );
}

function ViewActions({ activeProfileId, comparisonProfile, comparisonProfileId, document, editing, editAllocations, onComparisonProfileChange, onEdit, onEditAllocationsChange, onPortfolioValue, onShowUnallocatedChange, optionsButtonRef, profiles, saving, showUnallocated, view }) {
  const comparisonProfiles = sortProfilesByName(profiles.filter((profile) => profile.id !== activeProfileId));
  return <div className="view-page-actions">
    <ActionMenu>
      <ActionMenu.Button ref={optionsButtonRef}>Options</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Group selectionVariant="multiple">
            <ActionList.Item selected={showUnallocated} onSelect={() => onShowUnallocatedChange(!showUnallocated)}>Show unallocated</ActionList.Item>
            <ActionList.Item selected={editAllocations} disabled={saving || editing} onSelect={() => onEditAllocationsChange(!editAllocations)}>Edit allocations</ActionList.Item>
          </ActionList.Group>
          <ActionList.Divider />
          <ActionList.Item onSelect={onPortfolioValue}><ActionList.LeadingVisual><NumberIcon /></ActionList.LeadingVisual>Portfolio value…</ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
    {comparisonProfiles.length ? <ActionMenu>
      <ActionMenu.Button>{comparisonProfile ? `Compare: ${comparisonProfile.name}` : "Compare"}</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          <ActionList.Item selected={!comparisonProfileId} onSelect={() => onComparisonProfileChange(null)}>No comparison</ActionList.Item>
          {comparisonProfiles.map((profile) => <ActionList.Item key={profile.id} selected={profile.id === comparisonProfileId} onSelect={() => onComparisonProfileChange(profile.id)}>{profile.name}</ActionList.Item>)}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu> : null}
    <ActionMenu>
      <ActionMenu.Button>{view.name}</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {view.id ? <ActionList.LinkItem href="#/browser/all">All assets</ActionList.LinkItem> : <ActionList.Item selected>All assets</ActionList.Item>}
          {(document.browserViews ?? []).map((item) => item.id === view.id
            ? <ActionList.Item key={item.id} selected>{item.name}</ActionList.Item>
            : <ActionList.LinkItem href={`#/browser/${item.id}`} key={item.id}>{item.name}</ActionList.LinkItem>)}
          <ActionList.Divider />
          {document.properties.length
            ? <ActionList.LinkItem href="#/browser/new"><ActionList.LeadingVisual><PlusIcon /></ActionList.LeadingVisual>Add view</ActionList.LinkItem>
            : <ActionList.Item disabled>Add view after defining a property</ActionList.Item>}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
    {view.id && !editing ? <Button tone="secondary" leadingVisual={PencilIcon} onClick={onEdit} disabled={saving} labelDetail="view">Edit</Button> : null}
  </div>;
}

function browserViewDraft(view) {
  return { name: view.name, groupBy: [...view.groupBy], displayProperties: [...(view.displayProperties ?? [])], sort: view.sort, filter: view.filter ? { propertyId: view.filter.propertyId, valueId: view.filter.valueId } : null };
}

function ViewEditor({ document, draft, existingView, onCancel, onChange, onDelete, onSave, saveError, saving }) {
  const validationError = validateBrowserViewDraft(document, draft);
  const hasChanges = !existingView || JSON.stringify(draft) !== JSON.stringify(browserViewDraft(existingView));
  const displayProperties = draft.displayProperties ?? [];

  function changeGroup(index, propertyId) {
    const groupBy = [...draft.groupBy];
    if (!propertyId) groupBy.splice(index);
    else {
      groupBy[index] = propertyId;
      for (let nextIndex = groupBy.length - 1; nextIndex > index; nextIndex -= 1) {
        if (groupBy[nextIndex] === propertyId) groupBy.splice(nextIndex, 1);
      }
    }
    onChange({ ...draft, groupBy });
  }

  function changeFilterProperty(propertyId) {
    const property = document.properties.find((item) => item.id === propertyId);
    onChange({ ...draft, filter: property ? { propertyId, valueId: property.values[0].id } : null });
  }

  function changeDisplayProperty(index, propertyId) {
    const nextDisplayProperties = [...displayProperties];
    if (!propertyId) nextDisplayProperties.splice(index);
    else {
      nextDisplayProperties[index] = propertyId;
      for (let nextIndex = nextDisplayProperties.length - 1; nextIndex > index; nextIndex -= 1) {
        if (nextDisplayProperties[nextIndex] === propertyId) nextDisplayProperties.splice(nextIndex, 1);
      }
    }
    onChange({
      ...draft,
      displayProperties: nextDisplayProperties,
      sort: draft.sort === "decoration" && nextDisplayProperties.length === 0 ? "name" : draft.sort
    });
  }

  const filterProperty = draft.filter ? document.properties.find((property) => property.id === draft.filter.propertyId) : null;
  return (
    <FormContainer className="browser-view-editor">
      <FormBody className="view-editor-fields">
        <ViewField className="view-name-field" label="View name"><TextInput value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} placeholder="e.g. Portfolio by class" block /></ViewField>
        <div className="view-control-grid grouping-controls">
          {[0, 1, 2].map((index) => (
            <ViewField label={index === 0 ? "Group by" : "Then by"} key={index}>
              <Select aria-label={`Group level ${index + 1}`} value={draft.groupBy[index] ?? ""} onChange={(event) => changeGroup(index, event.target.value)} disabled={index > draft.groupBy.length} block>
                <Select.Option value="">{index === 0 ? "No grouping" : "No additional grouping"}</Select.Option>
                {document.properties.map((property) => <Select.Option value={property.id} key={property.id} disabled={draft.groupBy.some((propertyId, selectedIndex) => selectedIndex !== index && propertyId === property.id)}>{property.name}</Select.Option>)}
              </Select>
            </ViewField>
          ))}
        </div>
        <div className="view-control-grid decoration-controls">
          {[0, 1, 2].map((index) => (
            <ViewField label={index === 0 ? "Decorate with" : "Then with"} key={index}>
              <Select aria-label={`Asset decoration ${index + 1}`} value={displayProperties[index] ?? ""} onChange={(event) => changeDisplayProperty(index, event.target.value)} disabled={index > displayProperties.length} block>
                <Select.Option value="">{index === 0 ? "No decoration" : "No additional decoration"}</Select.Option>
                {document.properties.map((property) => <Select.Option value={property.id} key={property.id} disabled={displayProperties.some((propertyId, selectedIndex) => selectedIndex !== index && propertyId === property.id)}>{property.name}</Select.Option>)}
              </Select>
            </ViewField>
          ))}
        </div>
        <div className="view-control-grid analysis-controls">
          <ViewField label="Sort"><Select aria-label="Sort browser results" value={draft.sort} onChange={(event) => onChange({ ...draft, sort: event.target.value })} block><Select.Option value="allocation-desc">Allocation: high to low</Select.Option><Select.Option value="allocation-asc">Allocation: low to high</Select.Option><Select.Option value="configured">Configured property order</Select.Option>{displayProperties.length ? <Select.Option value="decoration">First decoration value</Select.Option> : null}<Select.Option value="name">Name: A to Z</Select.Option></Select></ViewField>
          <ViewField label="Filter by"><Select aria-label="Filter browser by property" value={filterProperty?.id ?? ""} onChange={(event) => changeFilterProperty(event.target.value)} block><Select.Option value="">All assets</Select.Option>{document.properties.map((property) => <Select.Option value={property.id} key={property.id}>{property.name}</Select.Option>)}</Select></ViewField>
          <ViewField label="Value"><Select aria-label="Filter browser by value" value={draft.filter?.valueId ?? ""} onChange={(event) => onChange({ ...draft, filter: { ...draft.filter, valueId: event.target.value } })} disabled={!filterProperty} block>{filterProperty ? filterProperty.values.map((value) => <Select.Option value={value.id} key={value.id}>{value.name}</Select.Option>) : <Select.Option value="">Any value</Select.Option>}</Select></ViewField>
        </div>
      </FormBody>
      <FormFooter className="view-editor-footer" hasChanges={hasChanges}>
        <div><Button onClick={onSave} disabled={!hasChanges || Boolean(validationError) || saving} labelDetail={saving ? undefined : "view"}>{saving ? "Saving…" : "Save"}</Button><Button tone="quiet" onClick={onCancel}>Cancel</Button></div>
        <Text className={`view-editor-message${saveError ? " error" : ""}`} role={saveError ? "alert" : "status"}>{saveError ?? validationError}</Text>
        {onDelete ? <Button className="form-danger-action" tone="danger" leadingVisual={TrashIcon} onClick={onDelete} labelDetail="view">Delete</Button> : null}
      </FormFooter>
    </FormContainer>
  );
}

function ViewField({ children, className, label }) {
  return <FormControl className={className}><FormControl.Label>{label}</FormControl.Label>{children}</FormControl>;
}

function DeleteViewConfirmation({ onCancel, onConfirm, saving, view }) {
  return <div className="view-delete-confirmation"><Alert tone="warning" heading={`Delete “${view.name}”?`}>Assets, allocations, and properties are not affected.</Alert><div><Button tone="quiet" onClick={onCancel}>Cancel</Button><Button tone="danger" onClick={onConfirm} disabled={saving} labelDetail={saving ? undefined : "view"}>{saving ? "Deleting…" : "Delete"}</Button></div></div>;
}

function ViewResults({ document, setup, title }) {
  const assets = filterAssets(document, setup.filter);
  return <section className="view-results">{title ? <Text className="view-results-heading">{title}</Text> : null}<BrowserResults assets={assets} document={document} setup={setup} /></section>;
}

function RememberedViewResults({ comparisonProfile, document, profile, profileId, setup, showUnallocated, viewId }) {
  const editing = useContext(AllocationEditingContext);
  const [expandedGroups, setExpandedGroups] = usePresentationState({
    defaultValue: {}, lifetime: "session", name: `expanded-groups:${encodeURIComponent(viewId)}`, page: "browser", profileId, scope: "profile", validate: booleanMap
  });
  const comparisonError = comparisonProfile ? comparisonCompatibilityError(document, comparisonProfile.data, setup) : null;
  const comparisonDocument = comparisonError ? null : comparisonProfile?.data;
  const assets = visibleAssets(document, setup.filter, showUnallocated, editing?.orderDocument, comparisonDocument);
  return <section className="view-results">
    {comparisonError ? <Alert tone="warning" heading={`Cannot compare with ${comparisonProfile.name}`}>{comparisonError}</Alert> : null}
    <BrowserResults assets={assets} comparisonDocument={comparisonDocument} comparisonName={comparisonProfile?.name} document={document} expandedGroups={expandedGroups} name={profile?.name} onExpandedGroupsChange={setExpandedGroups} onMapSelectionChange={(path) => setExpandedGroups(expandedGroupsForPath(path))} setup={setup} />
  </section>;
}

function BrowserResults({ assets, comparisonDocument, comparisonName, document, expandedGroups, name, onExpandedGroupsChange, onMapSelectionChange, setup }) {
  const orderDocument = useContext(AllocationEditingContext)?.orderDocument ?? document;
  const map = allocationMap(document, assets, setup, orderDocument);
  const impacts = assetComplianceImpacts(document, evaluateCompliance(document));
  return <>
    {!comparisonDocument ? <AllocationMap columns={map.columns} onSelectionChange={onMapSelectionChange} totalBasisPoints={map.totalBasisPoints} /> : null}
    <AssetImpactContext.Provider value={impacts}><BrowserTree assets={assets} comparisonDocument={comparisonDocument} comparisonName={comparisonName} document={document} expandedGroups={expandedGroups} name={name} onExpandedGroupsChange={onExpandedGroupsChange} setup={setup} /></AssetImpactContext.Provider>
  </>;
}

function expandedGroupsForPath(path) {
  return Object.fromEntries(path.map((_, index) => [path.slice(0, index + 1).join("/"), true]));
}

function BrowserTree({ assets, comparisonDocument, comparisonName, document, expandedGroups = {}, name, onExpandedGroupsChange, setup }) {
  const orderDocument = useContext(AllocationEditingContext)?.orderDocument ?? document;
  if (assets.length === 0) return <div className="browser-no-results"><strong>No assets to display</strong><Text>{document.assets.length ? "Choose another filter value or show all assets." : "Add assets to populate this view."}</Text></div>;
  const rootBasisPoints = allocationTotal(document, assets);
  const comparisonRootBasisPoints = comparisonDocument ? allocationTotal(comparisonDocument, assets) : undefined;
  const rootName = setup.filter ? "filtered view" : "portfolio";
  const property = document.properties.find((item) => item.id === setup.groupBy[0]);
  if (!property) return <div className="browser-tree" aria-label="Portfolio allocation browser"><AssetRows assets={sortAssets(orderDocument, assets, setup.sort, setup.displayProperties)} comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={comparisonRootBasisPoints} depth={0} displayProperties={setup.displayProperties} document={document} name={name} parentBasisPoints={rootBasisPoints} parentName={rootName} /></div>;
  const groups = withOffsets(groupsForProperty(document, assets, property, setup.sort, orderDocument, comparisonDocument));
  const dimmed = groups.some((group) => expandedGroups[`${group.propertyId}:${group.id}`]);
  return <div className="browser-tree" aria-label="Portfolio allocation browser">{groups.map((group) => <GroupNode comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={comparisonRootBasisPoints} depth={0} dimmed={dimmed} displayProperties={setup.displayProperties} document={document} expandedGroups={expandedGroups} group={group} groupBy={setup.groupBy} key={`${property.id}-${group.id}`} name={name} onExpandedGroupsChange={onExpandedGroupsChange} parentBasisPoints={rootBasisPoints} parentName={rootName} path={[]} sort={setup.sort} />)}</div>;
}

function GroupLevel({ assets, comparisonDocument, comparisonName, comparisonParentBasisPoints, depth, displayProperties, document, expandedGroups, groupBy, name, onExpandedGroupsChange, parentBasisPoints, parentName, path, sort }) {
  const orderDocument = useContext(AllocationEditingContext)?.orderDocument ?? document;
  const property = document.properties.find((item) => item.id === groupBy[depth]);
  if (!property) return <AssetRows assets={sortAssets(orderDocument, assets, sort, displayProperties)} comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={comparisonParentBasisPoints} depth={depth} displayProperties={displayProperties} document={document} name={name} parentBasisPoints={parentBasisPoints} parentName={parentName} />;
  const groups = withOffsets(groupsForProperty(document, assets, property, sort, orderDocument, comparisonDocument));
  const dimmed = groups.some((group) => expandedGroups[[...path, `${group.propertyId}:${group.id}`].join("/")]);
  return groups.map((group) => <GroupNode comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={comparisonParentBasisPoints} depth={depth} dimmed={dimmed} displayProperties={displayProperties} document={document} expandedGroups={expandedGroups} group={group} groupBy={groupBy} key={`${property.id}-${group.id}`} name={name} onExpandedGroupsChange={onExpandedGroupsChange} parentBasisPoints={parentBasisPoints} parentName={parentName} path={path} sort={sort} />);
}

function groupsForProperty(document, assets, property, sort, orderDocument, comparisonDocument) {
  const groups = property.values.map((value) => {
    const groupAssets = assets.filter((asset) => effectiveValue(document, asset.id, property.id).id === value.id);
    return { id: value.id, propertyId: property.id, name: value.name, assets: groupAssets, basisPoints: allocationTotal(document, groupAssets), comparisonBasisPoints: comparisonDocument ? allocationTotal(comparisonDocument, groupAssets) : undefined };
  }).filter((group) => group.assets.length > 0);
  return sortGroups(groups, sort, orderDocument);
}

function allocationMap(document, assets, setup, orderDocument) {
  const allocatedAssets = assets.filter((asset) => assetAllocation(document, asset.id) > 0);
  const totalBasisPoints = allocationTotal(document, allocatedAssets);
  if (!totalBasisPoints) return { columns: [], totalBasisPoints };

  let parents = [{ assets, basisPoints: totalBasisPoints, name: setup.filter ? "filtered view" : "portfolio", offsetBasisPoints: 0, path: [] }];
  const columns = [];
  for (const propertyId of setup.groupBy) {
    const property = document.properties.find((item) => item.id === propertyId);
    if (!property) break;
    const nodes = [];
    for (const parent of parents) {
      let offsetBasisPoints = parent.offsetBasisPoints;
      for (const group of groupsForProperty(document, parent.assets, property, setup.sort, orderDocument)) {
        if (!group.basisPoints) continue;
        const path = [...parent.path, `${property.id}:${group.id}`];
        nodes.push({
          ...group,
          href: propertyValueHref(property.id, group.id),
          label: group.name,
          offsetBasisPoints,
          parentBasisPoints: parent.basisPoints,
          parentName: parent.name,
          path
        });
        offsetBasisPoints += group.basisPoints;
      }
    }
    columns.push({ id: property.id, label: property.name, nodes });
    parents = nodes;
  }

  const assetNodes = [];
  for (const parent of parents) {
    let offsetBasisPoints = parent.offsetBasisPoints;
    for (const asset of sortAssets(orderDocument, parent.assets, setup.sort, setup.displayProperties)) {
      const basisPoints = assetAllocation(document, asset.id);
      if (!basisPoints) continue;
      assetNodes.push({
        basisPoints,
        href: assetHref(asset.id),
        label: asset.name || asset.id,
        leaf: true,
        offsetBasisPoints,
        parentBasisPoints: parent.basisPoints,
        parentName: parent.name,
        path: [...parent.path, `asset:${asset.id}`]
      });
      offsetBasisPoints += basisPoints;
    }
  }
  columns.push({ id: "assets", label: "Assets", nodes: assetNodes });
  return { columns, totalBasisPoints };
}

function GroupNode({ comparisonDocument, comparisonName, comparisonParentBasisPoints, depth, dimmed, displayProperties, document, expandedGroups, group, groupBy, name, onExpandedGroupsChange, parentBasisPoints, parentName, path, sort }) {
  const orderDocument = useContext(AllocationEditingContext)?.orderDocument ?? document;
  const nodePath = [...path, `${group.propertyId}:${group.id}`];
  const expansionKey = nodePath.join("/");
  const expanded = expandedGroups[expansionKey] ?? false;
  const property = document.properties.find((item) => item.id === group.propertyId);
  const content = <>
    <div className={`browser-tree-row group-row${expanded ? " expanded" : ""}${dimmed ? " dimmed" : ""}`}>
      <button className="tree-toggle" type="button" aria-expanded={expanded} aria-label={`${expanded ? "Collapse" : "Expand"} ${group.name}`} onClick={() => onExpandedGroupsChange?.((current) => ({ ...current, [expansionKey]: !expanded }))} />
      <div className="tree-primary">
        <ChevronRightIcon className="tree-disclosure" aria-hidden="true" />
        <div className="tree-main">
          <PropertyValueIdentity href={propertyValueHref(group.propertyId, group.id)} propertyHref={propertyHref(group.propertyId)} property={property} value={group} variant="browser" />
          <div className="tree-details"><AllocationComparison basisPoints={group.basisPoints} comparisonBasisPoints={group.comparisonBasisPoints} comparisonName={comparisonName} comparisonOffsetBasisPoints={group.comparisonOffsetBasisPoints} comparisonParentBasisPoints={comparisonParentBasisPoints} name={name} offsetBasisPoints={group.offsetBasisPoints} parentBasisPoints={parentBasisPoints} parentName={parentName} /></div>
        </div>
      </div>
    </div>
    {expanded ? <div className="tree-children">{depth + 1 < groupBy.length
      ? <GroupLevel assets={group.assets} comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={group.comparisonBasisPoints} depth={depth + 1} displayProperties={displayProperties} document={document} expandedGroups={expandedGroups} groupBy={groupBy} name={name} onExpandedGroupsChange={onExpandedGroupsChange} parentBasisPoints={group.basisPoints} parentName={group.name} path={nodePath} sort={sort} />
      : <AssetRows assets={sortAssets(orderDocument, group.assets, sort, displayProperties)} comparisonDocument={comparisonDocument} comparisonName={comparisonName} comparisonParentBasisPoints={group.comparisonBasisPoints} depth={depth + 1} displayProperties={displayProperties} document={document} name={name} parentBasisPoints={group.basisPoints} parentName={group.name} />}</div> : null}
  </>;
  const className = `browser-group depth-${Math.min(depth, 3)}`;
  return depth === 0
    ? <ExpansionSection className={className} expanded={expanded}>{content}</ExpansionSection>
    : <section className={className}>{content}</section>;
}

function booleanMap(value) {
  if (!value || Array.isArray(value) || typeof value !== "object") return null;
  return Object.fromEntries(Object.entries(value).filter(([, expanded]) => typeof expanded === "boolean"));
}

function booleanValue(value) {
  return typeof value === "boolean" ? value : null;
}

function stringValue(value) {
  return typeof value === "string" ? value : null;
}

function AssetRows({ assets, comparisonDocument, comparisonName, comparisonParentBasisPoints, depth, displayProperties = [], document, name, parentBasisPoints, parentName }) {
  const impacts = useContext(AssetImpactContext);
  const positionedAssets = withOffsets(assets.map((asset) => ({
    asset,
    basisPoints: assetAllocation(document, asset.id),
    comparisonBasisPoints: comparisonDocument ? assetAllocation(comparisonDocument, asset.id) : undefined
  })));
  return positionedAssets.map(({ asset, basisPoints, comparisonBasisPoints, comparisonOffsetBasisPoints, offsetBasisPoints }) => {
    const row = <div className={`browser-tree-row asset-row depth-${Math.min(depth, 3)}`} key={asset.id}>
      <div className="tree-primary"><div className="tree-main"><AssetIdentity asset={asset} href={assetHref(asset.id)} trailing={<ComplianceImpact impact={impacts.get(asset.id)} />} variant="responsive" /><div className="tree-details"><AllocationComparison assetId={asset.id} basisPoints={basisPoints} comparisonBasisPoints={comparisonBasisPoints} comparisonName={comparisonName} comparisonOffsetBasisPoints={comparisonOffsetBasisPoints} comparisonParentBasisPoints={comparisonParentBasisPoints} name={name} offsetBasisPoints={offsetBasisPoints} parentBasisPoints={parentBasisPoints} parentName={parentName} /><AssetDecorations assetId={asset.id} document={document} propertyIds={displayProperties} /></div></div></div>
    </div>;
    return row;
  });
}

function AllocationComparison({ assetId, basisPoints, comparisonBasisPoints, comparisonName, comparisonOffsetBasisPoints, comparisonParentBasisPoints, name, offsetBasisPoints, parentBasisPoints, parentName }) {
  const comparison = comparisonBasisPoints !== undefined;
  const { value: portfolioValue } = usePortfolioValue();
  return <span className={`allocation-comparison${comparison ? " compared" : ""}${portfolioValue !== null ? " with-portfolio-value" : ""}`}>
    <AllocationLine assetId={assetId} basisPoints={basisPoints} name={comparison ? name : null} offsetBasisPoints={offsetBasisPoints} parentBasisPoints={parentBasisPoints} parentName={parentName} portfolioValue={portfolioValue} />
    {comparison ? <AllocationLine basisPoints={comparisonBasisPoints} name={comparisonName} offsetBasisPoints={comparisonOffsetBasisPoints} parentBasisPoints={comparisonParentBasisPoints} parentName={parentName} portfolioValue={portfolioValue} /> : null}
  </span>;
}

function AllocationLine({ assetId, basisPoints, name, offsetBasisPoints, parentBasisPoints, parentName, portfolioValue }) {
  return <span className={`allocation-line${name ? " labelled" : ""}`}>
    {name ? <Text className="profile-label">{name}</Text> : null}
    <AllocationDisplay assetId={assetId} basisPoints={basisPoints} portfolioValue={portfolioValue} />
    <AllocationSlice basisPoints={basisPoints} offsetBasisPoints={offsetBasisPoints} parentBasisPoints={parentBasisPoints} parentName={parentName} />
  </span>;
}

function AllocationDisplay({ assetId, basisPoints, portfolioValue }) {
  const editing = useContext(AllocationEditingContext);
  return <strong className="tree-allocation"><span className="allocation-percentage">{assetId && editing ? <EditablePercentage alignment="start" clickableUnit disabled={editing.saving} label={`${assetId} allocation`} onSave={(nextValue) => editing.saveAllocation(assetId, nextValue)} value={basisPoints} /> : <span>{formatPercentage(basisPoints)}%</span>}</span>{portfolioValue !== null ? <span className="portfolio-amount">{formatPortfolioAmount(amountForBasisPoints(portfolioValue, basisPoints))}</span> : null}</strong>;
}

function AllocationSlice({ basisPoints, offsetBasisPoints, parentBasisPoints, parentName }) {
  const start = parentBasisPoints ? offsetBasisPoints / parentBasisPoints * 100 : 0;
  const size = parentBasisPoints ? basisPoints / parentBasisPoints * 100 : 0;
  const label = `${formatPercentage(size * 100)}% of ${parentName}`;
  return <span className="allocation-slice">
    <span className="slice-track" aria-hidden="true" style={{ "--slice-start": `${start}%`, "--slice-size": `${size}%` }}><span /></span>
    <Text className="slice-label">{label}</Text>
  </span>;
}

function withOffsets(items) {
  let offsetBasisPoints = 0;
  let comparisonOffsetBasisPoints = 0;
  return items.map((item) => {
    const positioned = { ...item, comparisonOffsetBasisPoints, offsetBasisPoints };
    offsetBasisPoints += item.basisPoints;
    comparisonOffsetBasisPoints += item.comparisonBasisPoints ?? 0;
    return positioned;
  });
}

function AllocationWarning({ document, editing = false }) {
  if (document.assets.length === 0) return null;
  const total = allocationTotal(document, document.assets);
  if (editing) return <Text className="browser-allocation-status">Total: {formatPercentage(total)}% · {total === 10000 ? "Fully allocated" : `${formatPercentage(Math.abs(10000 - total))}% ${total < 10000 ? "left to allocate" : "overallocated"}`}</Text>;
  if (total === 10000) return null;
  const difference = Math.abs(10000 - total);
  return <Alert tone="warning" heading={`Portfolio allocation totals ${formatPercentage(total)}%`}>{formatPercentage(difference)}% is {total < 10000 ? "unallocated" : "overallocated"}. <a className="allocation-warning-link" href="#/allocation">Edit allocation</a></Alert>;
}

function viewDescription(document, view) {
  const groups = view.groupBy.map((propertyId) => document.properties.find((property) => property.id === propertyId)?.name).filter(Boolean).join(" → ") || "All assets";
  const decorationProperty = document.properties.find((property) => property.id === view.displayProperties?.[0]);
  const sort = { "allocation-desc": "Allocation high to low", "allocation-asc": "Allocation low to high", configured: "Configured order", decoration: `${decorationProperty?.name ?? "First decoration"} value order`, name: "Name A to Z" }[view.sort];
  if (!view.filter) return `${groups} · ${sort}`;
  const property = document.properties.find((item) => item.id === view.filter.propertyId);
  const value = property?.values.find((item) => item.id === view.filter.valueId);
  return `${groups} · ${sort} · ${property?.name}: ${value?.name}`;
}

function filterAssets(document, filter) { return filter ? document.assets.filter((asset) => effectiveValue(document, asset.id, filter.propertyId).id === filter.valueId) : document.assets; }
function visibleAssets(document, filter, showUnallocated, orderDocument, comparisonDocument) { return filterAssets(document, filter).filter((asset) => showUnallocated || assetAllocation(document, asset.id) > 0 || (orderDocument && assetAllocation(orderDocument, asset.id) > 0) || (comparisonDocument && assetAllocation(comparisonDocument, asset.id) > 0)); }
function sortGroups(groups, sort, orderDocument) { if (sort === "allocation-desc") return [...groups].sort((left, right) => allocationTotal(orderDocument, right.assets) - allocationTotal(orderDocument, left.assets)); if (sort === "allocation-asc") return [...groups].sort((left, right) => allocationTotal(orderDocument, left.assets) - allocationTotal(orderDocument, right.assets)); if (sort === "name") return [...groups].sort((left, right) => left.name.localeCompare(right.name)); return groups; }
function sortAssets(document, assets, sort, displayProperties = []) {
  const comparedAssets = [...assets];
  if (sort === "allocation-desc") return comparedAssets.sort((left, right) => assetAllocation(document, right.id) - assetAllocation(document, left.id));
  if (sort === "allocation-asc") return comparedAssets.sort((left, right) => assetAllocation(document, left.id) - assetAllocation(document, right.id));
  if (sort === "decoration") {
    const property = document.properties.find((item) => item.id === displayProperties[0]);
    if (property) return comparedAssets.sort((left, right) => propertyValueIndex(document, property, left.id) - propertyValueIndex(document, property, right.id) || compareAssetNames(left, right));
  }
  if (sort === "name") return comparedAssets.sort(compareAssetNames);
  return assets;
}
function propertyValueIndex(document, property, assetId) { return property.values.findIndex((value) => value.id === effectiveValue(document, assetId, property.id).id); }
function compareAssetNames(left, right) { return (left.name || left.id).localeCompare(right.name || right.id, undefined, { numeric: true, sensitivity: "base" }) || left.id.localeCompare(right.id, undefined, { numeric: true, sensitivity: "base" }); }
function allocationTotal(document, assets) { return assets.reduce((sum, asset) => sum + assetAllocation(document, asset.id), 0); }
function assetAllocation(document, assetId) { return (document.allocations ?? []).find((allocation) => allocation.assetId === assetId)?.basisPoints ?? 0; }
function formatPercentage(basisPoints) { return (basisPoints / 100).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1"); }
function comparisonCompatibilityError(document, comparisonDocument, setup) {
  const assetIds = document.assets.map((asset) => asset.id).sort();
  const comparisonAssetIds = comparisonDocument.assets.map((asset) => asset.id).sort();
  if (JSON.stringify(assetIds) !== JSON.stringify(comparisonAssetIds)) return "Both profiles must contain the same assets before their Browser trees can be compared.";

  const propertyIds = [...new Set([...setup.groupBy, ...(setup.displayProperties ?? []), setup.filter?.propertyId].filter(Boolean))];
  for (const propertyId of propertyIds) {
    const property = document.properties.find((item) => item.id === propertyId);
    const comparisonProperty = comparisonDocument.properties.find((item) => item.id === propertyId);
    if (!comparisonProperty) return "Both profiles must share the properties used by this Browser view.";
    if (JSON.stringify(property.values.map((value) => value.id)) !== JSON.stringify(comparisonProperty.values.map((value) => value.id))) {
      return "Both profiles must share the property values used by this Browser view.";
    }
    if (document.assets.some((asset) => effectiveValue(document, asset.id, propertyId).id !== effectiveValue(comparisonDocument, asset.id, propertyId).id)) {
      return "Both profiles must classify assets the same way for the properties used by this Browser view.";
    }
  }
  return null;
}

function nullableStringValue(value) { return value === null || typeof value === "string" ? value : undefined; }

function MissingView() { return <article className="browser-page"><PageHeading description="This saved view no longer exists." actions={<Button as="a" href="#/browser" tone="secondary">Open Browser</Button>}>View not found</PageHeading></article>; }
function BrowserLoading() { return <article className="browser-page"><PageHeading description="Loading portfolio browser…">Browser</PageHeading></article>; }
